import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createSparePart, updateSparePart } from '../../redux/features/sparePartSlice';
import { toast } from 'react-hot-toast';
import { fetchDeviceCategories } from '../../redux/api';
import SupplierSelect from '../inventory/SupplierSelect';

const SparePartForm = ({ isEdit, sparePart, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    price: '',
    purchasePrice: '',
    quantity: '',
    minQuantity: '',
    category: '',
    deviceCategoryId: '',
    supplierId: '',
    supplierName: ''
  });
  const [deviceCategories, setDeviceCategories] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchDeviceCategories();
        const categories = Array.isArray(response?.data) ? response.data : [];
        console.log('🔩 [SparePartForm] Učitane kategorije uređaja za rezervne delove:', categories);
        setDeviceCategories(categories);
      } catch (error) {
        console.error('Greška pri učitavanju kategorija:', error);
        toast.error('Ne možemo da učitamo kategorije');
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (isEdit && sparePart) {
      setFormData({
        ...sparePart,
        deviceCategoryId:
          sparePart.deviceCategory?.id ||
          sparePart.deviceCategory?._id ||
          sparePart.deviceCategory ||
          '',
        supplierId: sparePart.supplierId || '',
        supplierName: sparePart.supplierName || sparePart.arrivalSource || ''
      });
    }
  }, [isEdit, sparePart]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (only "name" is mandatory)
    const requiredFields = ['name'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast.error(`Nedostaju obavezna polja: ${missingFields.join(', ')}`);
      return;
    }

    try {
      console.log('🔩 [SparePartForm] Slanje rezervnog dela na backend:', {
        ...formData,
        // For logs only; do not send NaN values.
        price: formData.price !== '' ? parseFloat(formData.price) : undefined,
        purchasePrice: formData.purchasePrice !== '' ? parseFloat(formData.purchasePrice) : undefined,
        quantity: formData.quantity !== '' ? parseInt(formData.quantity, 10) : undefined,
        minQuantity: formData.minQuantity !== '' ? parseInt(formData.minQuantity, 10) : undefined
      });

      // Only include optional numeric fields when user actually provided a value.
      const payload = { ...formData };
      if (payload.code === '') delete payload.code;
      if (payload.price === '') delete payload.price;
      if (payload.purchasePrice === '') delete payload.purchasePrice;
      if (payload.quantity === '') delete payload.quantity;
      if (payload.minQuantity === '') delete payload.minQuantity;
      if (payload.category === '') delete payload.category;
      if (payload.deviceCategoryId === '') delete payload.deviceCategoryId;
      if (payload.supplierId === '') delete payload.supplierId;
      if (payload.supplierName === '') delete payload.supplierName;
      delete payload.arrivalSourceId;
      delete payload.arrivalSource;

      if (payload.price !== undefined) payload.price = parseFloat(payload.price);
      if (payload.purchasePrice !== undefined) payload.purchasePrice = parseFloat(payload.purchasePrice);
      if (payload.quantity !== undefined) payload.quantity = parseInt(payload.quantity, 10);
      if (payload.minQuantity !== undefined) payload.minQuantity = parseInt(payload.minQuantity, 10);

      // Don't send empty strings for optional text fields.
      if (payload.description === '') delete payload.description;

      if (isEdit) {
        await dispatch(updateSparePart({ id: sparePart._id, ...payload })).unwrap();
        toast.success('Rezervni deo je uspešno ažuriran');
      } else {
        await dispatch(createSparePart(payload)).unwrap();
        toast.success('Rezervni deo je uspešno kreiran');
      }
      onClose();
    } catch (error) {
      toast.error(error.message || 'Greška pri čuvanju rezervnog dela');
    }
  };

  const baseInputClass = "w-full border border-gray-600 rounded px-2 py-1.5 text-xs text-white bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-shadow h-8";
  const labelClass = "block text-xs font-medium text-white mb-1";

  return (
    <div className="bg-gray-800 rounded-md shadow-sm mx-auto">
      <h2 className="text-xs font-semibold py-1.5 px-2 border-b border-gray-700 text-white flex items-center">
        {isEdit ? 'Izmena rezervnog dela' : 'Dodavanje novog rezervnog dela'}
      </h2>
      
      <form onSubmit={handleSubmit} className="px-2 py-1.5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Column 1: Basic Information */}
          <div className="bg-gray-700 p-1.5 rounded-md">
            <h3 className="text-xs font-semibold mb-1.5 text-white flex items-center">
              Osnovni podaci
            </h3>
            
            <div className="space-y-0">
              <div className="mb-2">
                <label className={labelClass}>Naziv *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={baseInputClass}
                  required
                />
              </div>

              <div className="mb-2">
                <label className={labelClass}>Šifra</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className={baseInputClass}
                />
              </div>

              <div className="mb-2">
                <label className={labelClass}>Kategorija uređaja</label>
                <select
                  name="deviceCategoryId"
                  value={formData.deviceCategoryId}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = deviceCategories.find(
                      (cat) => (cat.id || cat._id) === value
                    );
                    setFormData(prev => ({
                      ...prev,
                      deviceCategoryId: value,
                      // Zadržavamo i string kategoriju zbog kompatibilnosti,
                      // ali jedinstveni identifikator je deviceCategoryId.
                      category: selected?.name || prev.category
                    }));
                  }}
                  className={baseInputClass}
                >
                  <option value="">Izaberi kategoriju</option>
                  {deviceCategories.map(category => (
                    <option key={category.id || category._id} value={category.id || category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label className={labelClass}>Opis</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`${baseInputClass} h-[60px]`}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Column 2: Pricing and Inventory */}
          <div className="bg-gray-700 p-1.5 rounded-md">
            <h3 className="text-xs font-semibold mb-1.5 text-white flex items-center">
              Cene i zalihe
            </h3>
            
            <div className="space-y-0">
              <div className="grid grid-cols-2 gap-2">
                <div className="mb-2">
                  <label className={labelClass}>Cena</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className={baseInputClass}
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="mb-2">
                  <label className={labelClass}>Nabavna cena</label>
                  <input
                    type="number"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    className={baseInputClass}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="mb-2">
                <label className={labelClass}>Količina</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  className={baseInputClass}
                  min="0"
                />
              </div>

              <div className="mb-2">
                <label className={labelClass}>Dobavljač</label>
                <SupplierSelect
                  valueId={formData.supplierId}
                  valueName={formData.supplierName}
                  onChange={({ id, name }) => setFormData((prev) => ({
                    ...prev,
                    supplierId: id,
                    supplierName: name
                  }))}
                />
              </div>

              <div className="mb-2">
                <label className={labelClass}>Min. količina za obaveštenje</label>
                <input
                  type="number"
                  name="minQuantity"
                  value={formData.minQuantity}
                  onChange={handleChange}
                  className={baseInputClass}
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-2 pt-1.5 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Otkaži
          </button>
          <button
            type="submit"
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            {isEdit ? 'Sačuvaj izmene' : 'Dodaj'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SparePartForm;